import { useEffect, useRef } from 'react';
import { useWeb3React } from '@web3-react/core';
import { useWallet } from '../contexts/WalletContext';
import { metaMask } from '../config/connectors';

/**
 * Wallet Initialization Hook
 * Handles the automatic reconnection logic when the application starts
 */
export const useWalletInit = () => {
  const { isActive, connector } = useWeb3React();
  const { account: storedAccount, isConnected: storedIsConnected } = useWallet();
  const hasTriedReconnect = useRef(false);
  const isInitialMount = useRef(true);
  const reconnectTimer = useRef<NodeJS.Timeout | null>(null);

  // Clear timer
  useEffect(() => {
    return () => {
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
    };
  }, []);

  // Main reconnection logic
  useEffect(() => {
    console.log('🔄 Wallet initialization check', {
      isActive,
      connector: connector?.constructor?.name,
      hasTriedReconnect: hasTriedReconnect.current,
      isInitialMount: isInitialMount.current
    });

    // If it is not the initial mount, skip
    if (!isInitialMount.current) {
      return;
    }

    // If you have tried to reconnect, do not repeat
    if (hasTriedReconnect.current) {
      return;
    }

    // If it is already connected, mark it as having tried to reconnect
    if (isActive) {
      console.log('✅ Wallet connected, marking reconnection as complete');
      hasTriedReconnect.current = true;
      isInitialMount.current = false;
      return;
    }

    // Check if there is a previous connection state
     const checkAndReconnect = async () => {
       console.log('📦 Check storage status:', {
         storedAccount: !!storedAccount,
         storedIsConnected
       });

       // If there is no stored account or it is not connected, it means that there was no valid connection before
       if (!storedAccount || !storedIsConnected) {
         console.log('❌ No valid connection state, skip reconnection');
         hasTriedReconnect.current = true;
         return;
       }

      console.log('🔌 Trying to automatically reconnect the wallet...');
      hasTriedReconnect.current = true;

      try {
        // Use a more direct method: first check if MetaMask is available
         if (typeof window.ethereum !== 'undefined') {
           console.log('🦊 MetaMask detected');
           
           // Try to get the current account
           const accounts = await (window.ethereum as any).request({
             method: 'eth_accounts'
           });
          
          console.log('📋 Current account:', accounts);
          
          if (accounts && accounts.length > 0) {
            console.log('🔌 Found authorized account, trying to connect');
            await metaMask.activate();
            console.log('✅ Wallet reconnected successfully');
          } else {
             console.log('❌ No authorized account');
             // Do not clear the storage state, keep the user's connection preferences
           }
         } else {
           console.log('❌ MetaMask not detected');
         }
       } catch (error) {
         console.error('❌ Automatic reconnection failed:', error);
         // Do not clear the storage state, keep the user's connection preferences
       }
    };

    // Delay the execution of the reconnection check to ensure that the page is fully loaded
    reconnectTimer.current = setTimeout(() => {
      checkAndReconnect();
      isInitialMount.current = false;
    }, 1000);

  }, [isActive, storedAccount, storedIsConnected]);

  // Listen for connection status changes for debugging
  useEffect(() => {
    console.log('Wallet connection status changed:', { isActive });
  }, [isActive]);
};

export default useWalletInit;